import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';

interface ProductOption {
  value: string;
  label: string;
}

const useBillingProductsPicker = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const { token } = await identityApi.getCredentials();

        const response = await fetch(
          `${baseUrl}/api/billing-portal/proxy/products.get`,
          {
            method: 'POST',
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                }
              : {
                  'Content-Type': 'application/json',
                },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }

        const data = await response.json();

        // Assuming the API returns an array of products with id and name fields
        const productOptions: ProductOption[] = (data || []).map(
          (product: any) => ({
            value: product.id,
            label: product.plan_name,
          }),
        );
        productOptions.sort((a, b) => a.label.localeCompare(b.label));

        setOptions(productOptions);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch products',
        );
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [configApi, identityApi]);

  return { options, loading, error };
};

export default useBillingProductsPicker;
